// Hand-written intro + SEO copy for every collection handle in navigation.ts
// Voice: warm, konkret, butiks-nært. Plain Danish. English mirrors the Danish.
// - `intro`: 1–2 sætninger over produktnettet (max ~180 tegn for SEO meta)
// - `seo.heading` + `seo.body`: længere tekst under produktnettet
//   (paragraffer adskilles med tomme linjer)
// - `seo.bullets` (valgfri): 3–5 hurtige facts/tips

import type { Locale } from "./i18n";

export type CategoryCopy = {
  /** Short, scannable intro shown above the product grid. Blank lines = paragraphs. */
  intro: string;
  /** Overrides the generated <title> */
  metaTitle?: string;
  /** Overrides the meta description (otherwise derived from intro) */
  metaDescription?: string;
  /** H1 override (otherwise the collection title) */
  heading?: string;
  /** Collection-specific FAQ, replacing the generic one */
  faq?: { q: string; a: string }[];
  /** Heading above the FAQ (otherwise the generic one) */
  faqTitle?: string;
  seo: {
    heading: string;
    /** Newlines = paragraphs. Keep paragraphs short. */
    body: string;
    bullets?: string[];
  };
};

type CopyMap = Record<string, { da: CategoryCopy; en: CategoryCopy }>;

// Shared building blocks reused across many categories
const SHIPPING_DA =
  "Vi sender hver mandag fra showroomet på Frederiksberg, og vi pakker omhyggeligt i avis, bobleplast og ekstra pap. Sender til hele verden.";
const SHIPPING_EN =
  "We dispatch every Monday from our Frederiksberg showroom, packed carefully in paper, bubble wrap and extra cardboard. Worldwide shipping.";

const VIEWING_DA =
  "Du er altid velkommen til at bestille en fremvisning, hvis du vil se varerne i virkeligheden, før du køber.";
const VIEWING_EN =
  "You are always welcome to book a viewing if you want to see the pieces in person before you buy.";

export const categoryCopy: CopyMap = {
  // ──────────────────────────────────────────────────────────
  //  FUND FRA JAPAN
  // ──────────────────────────────────────────────────────────
  "fund-fra-japan": {
    da: {
      intro:
        "I 2026 pakkede vi kufferten og tog til Japan – gennem Tokyos neonlys, det stille og eftertænksomme Hiroshima, tempel- og haveidyllen i Kyoto og de gode madoplevelser i Osaka. På loppemarkeder, i genbrugsbutikker og små baggader fandt vi en masse smukke ting med sjæl: tallerkener, skåle, glas og keramik, som andre har spist, drukket og levet med i årevis. Nu har de fået en ny rejse til jer – hvert stykke med sin egen lille historie og et strejf af Japan.",
      seo: {
        heading: "Fund fra Japan",
        body:
          "Alt i kollektionen er hentet hjem af os selv og båret med i kufferten fra Japan. Der er kun ét eksemplar af hvert stykke, så når det er væk, er det væk.\n\nTingene er brugte og bærer præg af et liv før vores. Små brugsspor og patina hører med — vi skriver det tydeligt på produktsiden, hvis der er noget at være opmærksom på.\n\n" +
          SHIPPING_DA +
          "\n\n" +
          VIEWING_DA,
      },
    },
    en: {
      intro:
        "In 2026 we packed our suitcases and travelled through Japan – the neon of Tokyo, the quiet, reflective Hiroshima, the temples and gardens of Kyoto and the food of Osaka. At flea markets, in second-hand shops and small back streets we found beautiful things with soul: plates, bowls, glass and ceramics that others have eaten, drunk and lived with for years. Now they are off on a new journey to you – each piece with its own little story and a touch of Japan.",
      seo: {
        heading: "Finds from Japan",
        body:
          "Everything in this collection was found and carried home by us from Japan. There is only one of each piece, so when it is gone, it is gone.\n\nThe pieces are second-hand and carry a life before ours. Small marks of use and patina are part of the charm — we always note anything worth knowing on the product page.\n\n" +
          SHIPPING_EN +
          "\n\n" +
          VIEWING_EN,
      },
    },
  },

  // ──────────────────────────────────────────────────────────
  //  MURANO
  // ──────────────────────────────────────────────────────────
  "murano-lamper": {
    da: {
      intro:
        "Hos Farveladeland finder du et håndplukket udvalg af vintage Murano lamper i italiensk glas. Hver lampe er udvalgt én for én, fordi den har noget særligt i farve, form, håndværk eller udtryk. Det betyder, at du ikke køber en masseproduceret lampe, men en unik lyskilde med historie, personlighed og masser af karakter.",
      seo: {
        heading: "Vintage Murano-lamper hos Farveladeland",
        body:
          "Murano-lamper er mundblæst på øen uden for Venedig, og hver lampe er et stykke italiensk håndværk. Vi køber hovedsageligt fra danske hjem og auktioner, så lamperne du finder her er ægte vintage — typisk fra 1960'erne, 70'erne og 80'erne.\n\nUdvalget skifter løbende. Vi får nye lamper ind hver uge, og populære modeller som mushroom-lamperne bliver ofte revet væk samme dag. Hold øje med 'Netop landet', hvis du leder efter noget bestemt.\n\n" +
          SHIPPING_DA +
          "\n\n" +
          VIEWING_DA,
        bullets: [
          "Mundblæst glas fra Murano, Italien",
          "Vintage — typisk fra 1960–1985",
          "Tjekket og rengjort før forsendelse",
          "Spar 15–45% i Murano-outlettet",
        ],
      },
    },
    en: {
      intro:
        "Our full range of vintage Murano lamps — ceiling, chandeliers, table, wall and mushroom lamps in hand-blown glass from the island of Murano.",
      seo: {
        heading: "Vintage Murano lamps at Farveladeland",
        body:
          "Murano lamps are hand-blown on the island just outside Venice, and every lamp is a piece of Italian craft. We mostly source from Danish homes and auctions, so what you find here is genuine vintage — typically from the 1960s, 70s and 80s.\n\nThe selection changes weekly. New lamps land all the time, and popular pieces like the mushroom lamps often sell within the day. Keep an eye on 'Just landed' if you are looking for something specific.\n\n" +
          SHIPPING_EN +
          "\n\n" +
          VIEWING_EN,
        bullets: [
          "Hand-blown glass from Murano, Italy",
          "Vintage — typically 1960–1985",
          "Checked and cleaned before shipping",
          "Save 15–45% in the Murano outlet",
        ],
      },
    },
  },

  "glas-lamper": {
    da: {
      metaTitle: "Glas lampe | vintage glas lamper hos Farveladeland",
      metaDescription:
        "Køb håndplukket glas lampe hos Farveladeland. Find vintage glaslamper, Murano lamper og unikke lamper i glas til hjemmet med farve, sjæl og varme.",
      heading: "Glas lampe",
      intro:
        "Hos Farveladeland finder du håndplukkede glaslamper med farve og charme. En glas lampe kan være alt fra en lille bordlampe til en større loftlampe, men fælles for dem er, at glasset giver lyset et særligt udtryk.",
      seo: {
        // The long-form copy lives in the collection story (collection-stories.tsx).
        heading: "Glas lampe",
        body: "",
      },
      faq: [
        {
          q: "Hvad er en glas lampe?",
          a: "En glas lampe er en lampe, hvor skærmen eller en stor del af lampens udtryk er lavet i glas. Det kan være en bordlampe, loftlampe, væglampe, pendel eller mushroom lampe.",
        },
        {
          q: "Hvilke typer glaslamper findes der?",
          a: "Glaslamper findes i mange former. Du kan blandt andet finde bordlamper i glas, pendler, loftlamper, væglamper og dekorative mushroom lamper. Udtrykket afhænger af både form, farve, glasstruktur og placering.",
        },
        {
          q: "Hvad er forskellen på en glas lampe og en glas pendel?",
          a: "En glas pendel er en bestemt type glaslampe, der hænger ned fra loftet. En glas lampe er en bredere betegnelse, som også kan dække bordlamper, væglamper, plafonder og andre lamper i glas.",
        },
        {
          q: "Hvor passer en glaslampe bedst?",
          a: "Glaslamper kan bruges i mange rum. En bordlampe i glas fungerer godt på et natbord, en kommode eller i stuen, mens en glaspendel kan hænge over spisebordet, i køkkenet eller i gangen. Væglamper i glas er gode, hvis du vil spare plads.",
        },
        {
          q: "Er jeres glaslamper vintage?",
          a: "Mange af vores glaslamper er vintage eller second-hand fund, som vi selv udvælger. Udvalget skifter løbende, og flere lamper findes kun i ét eller få eksemplarer.",
        },
        {
          q: "Hvordan pakker I glaslamper?",
          a: "Vi pakker glaslamper grundigt og forsvarligt, så de er bedst muligt beskyttet under fragt. Da mange af lamperne er lavet i glas og findes i få eksemplarer, bruger vi ekstra tid på indpakningen.",
        },
        {
          q: "Kan jeg se lampen først?",
          a: "Det kan som regel godt lade sig gøre efter aftale. Mange vil gerne se farve, størrelse og glasstruktur i virkeligheden, før de køber. Kontakt os via kontaktformularen eller Instagram og book en tid til en fremvisning.",
        },
      ],
    },
    en: {
      heading: "Glass lamps",
      intro:
        "Vintage glass lamps — Murano and other hand-blown glass lamps, all in one place. Hand-picked in Copenhagen, shipped worldwide.",
      seo: {
        heading: "Glass lamps at Farveladeland",
        body: SHIPPING_EN + "\n\n" + VIEWING_EN,
      },
    },
  },

  "italienske-lamper": {
    da: {
      metaTitle: "Italienske lamper | Vintage lamper hos Farveladeland",
      metaDescription:
        "Køb håndplukkede italienske lamper hos Farveladeland. Find vintage bordlamper, loftlamper, væglamper og farverige Murano lamper til hjemmet med sjæl og liv.",
      heading: "Italienske lamper",
      intro:
        "Hos Farveladeland finder du specielt udvalgte italienske lamper med farve, personlighed og charme. Italien er kendt for lamper, hvor materialer og former får lov til at fylde mere end det anonyme og masseproducerede. Derfor passer italienske lamper godt ind i hjem, hvor belysning gerne må være en del af indretningen og ikke bare noget praktisk.",
      seo: {
        // The long-form copy lives in the collection story (collection-stories.tsx).
        heading: "Italienske lamper",
        body: "",
      },
      faqTitle: "Ofte stillede spørgsmål om italienske lamper",
      faq: [
        {
          q: "Hvad kendetegner italienske lamper?",
          a: "Italienske lamper er ofte kendetegnet ved et mere dekorativt formsprog, gode materialer og en tydelig fornemmelse for design. Mange vintage lamper fra Italien har et personligt udtryk, hvor lampen ikke kun fungerer som belysning, men også som en del af indretningen.",
        },
        {
          q: "Er italienske lamper det samme som Murano lamper?",
          a: "Murano lamper er en type italienske lamper, men italienske lamper dækker bredere. Murano lamper er især kendt for mundblæst glas fra Murano-området ved Venedig, mens italienske lamper også kan være lavet i andre materialer, former og stilarter.",
        },
        {
          q: "Hvilke typer italienske lamper kan jeg finde hos Farveladeland?",
          a: "Udvalget skifter løbende, men du kan finde forskellige typer italienske lamper hos Farveladeland. Det kan blandt andet være bordlamper, loftlamper, væglamper, mushroom lamper, lysekroner og lamper i farvet glas.",
        },
        {
          q: "Er jeres italienske lamper vintage?",
          a: "Mange af vores italienske lamper er vintage eller second-hand fund, som vi selv udvælger. Det betyder også, at udvalget ændrer sig løbende, og at flere lamper kun findes i ét eller få eksemplarer.",
        },
        {
          q: "Hvordan vælger jeg den rigtige italienske lampe?",
          a: "Det afhænger især af rummet, størrelsen og den stemning, du gerne vil skabe. En bordlampe er god til små lyszoner, en loftlampe kan samle rummet, og en væglampe giver belysning uden at bruge gulv- eller bordplads. Farve, materiale og placering betyder også meget for, hvordan lampen opleves i hjemmet. Find inspiration til at vælge den rigtige lampe.",
        },
        {
          q: "Hvordan pakker I italienske lamper?",
          a: "Vi pakker italienske lamper grundigt og forsvarligt, så de er bedst muligt beskyttet under fragt. Da mange af vores lamper er unikke vintagefund, bruger vi ekstra tid på indpakningen.",
        },
        {
          q: "Kan jeg se lampen først?",
          a: "Det kan som regel godt lade sig gøre efter aftale. Mange vil gerne se farve, størrelse og stand i virkeligheden, før de køber. Kontakt os og besøg vores showroom efter aftale.",
        },
      ],
    },
    en: {
      heading: "Italian lamps",
      intro:
        "Vintage Italian lamps — Murano and other lamps from Italy, all in one place. Hand-picked in Copenhagen, shipped worldwide.",
      seo: {
        heading: "Italian lamps at Farveladeland",
        body: SHIPPING_EN + "\n\n" + VIEWING_EN,
      },
    },
  },

  "glas-pendel": {
    da: {
      metaTitle: "Glas pendel | vintage glas pendler hos Farveladeland",
      metaDescription:
        "Køb håndplukket glas pendel hos Farveladeland. Find vintage pendler, Murano glaslamper og loftlamper med farve, sjæl og smukt lys til hjemmet og hyggekrog.",
      heading: "Glas pendel",
      intro:
        "Hos Farveladeland finder du håndplukkede glas pendler med farve, sjæl og vintagecharme. En glas pendel er oplagt, hvis du vil have en lampe, der ikke bare lyser rummet op, men også giver indretningen et mere personligt udtryk.",
      seo: {
        // The long-form copy lives in the collection story (collection-stories.tsx).
        heading: "Glas pendel",
        body: "",
      },
      faqTitle: "Ofte stillede spørgsmål om glas pendler",
      faq: [
        {
          q: "Hvad er en glas pendel?",
          a: "En glas pendel er en hængelampe, hvor selve skærmen eller en stor del af lampens udtryk er lavet i glas. Den hænger typisk ned fra loftet og bruges ofte over spisebordet, i køkkenet, i gangen eller i et hjørne af stuen.",
        },
        {
          q: "Hvad er forskellen på en glas pendel og en loftlampe?",
          a: "En pendel er en type loftlampe, der hænger ned fra loftet. Andre loftlamper kan for eksempel være plafonder eller lamper, der sidder tættere på loftet. Derfor giver pendlen ofte et mere markeret fokuspunkt i rummet.",
        },
        {
          q: "Hvor passer en glas pendel bedst?",
          a: "Denne type lampe passer godt over spisebordet, i køkkenet, i entréen, på gangen eller i et lille hjørne, hvor du gerne vil skabe stemning. Mindre pendler kan også hænge flere sammen, hvis du vil skabe et mere dekorativt udtryk.",
        },
        {
          q: "Kan en glas pendel bruges over spisebordet?",
          a: "En glas pendel kan sagtens bruges over spisebordet, hvis størrelse og lys passer til bordet. En større pendel kan samle spisepladsen, mens flere mindre pendler kan give et mere let og levende udtryk.",
        },
        {
          q: "Er jeres glas pendler vintage?",
          a: "Mange af vores glas pendler er vintage eller second-hand fund, som vi selv udvælger. Udvalget skifter løbende, og flere lamper findes kun i ét eller få eksemplarer.",
        },
        {
          q: "Hvordan pakker I glas pendler?",
          a: "Vi pakker glas pendler grundigt og forsvarligt, så de er bedst muligt beskyttet under fragt. Da mange af lamperne er lavet i glas og findes i få eksemplarer, bruger vi ekstra tid på indpakningen.",
        },
      ],
    },
    en: {
      heading: "Glass pendants",
      intro:
        "Vintage glass pendant lamps, Murano and other hand-blown glass, hand-picked in Copenhagen and shipped worldwide.",
      seo: {
        heading: "Glass pendants at Farveladeland",
        body: SHIPPING_EN + "\n\n" + VIEWING_EN,
      },
    },
  },

  "vaeglamper-i-glas": {
    da: {
      metaTitle: "Væglampe i glas | Vintage og retro | Farveladeland",
      metaDescription:
        "Find en væglampe i glas hos Farveladeland. Vintage og retro væglamper i opalglas, farvet og mundblæst glas, ofte med messing. Håndplukket i København.",
      heading: "Væglamper i glas",
      intro:
        "Hos Farveladeland finder du vintage væglamper i glas, fra mælkehvidt opalglas til mundblæst Murano og farvede skærme med beslag i messing. Hver lampe er gennemgået, før den kommer i shoppen. En væglampe i glas giver blødt lys uden at tage plads på bordet, og den gør sig lige godt i entréen, stuen og soveværelset.",
      seo: {
        // The long-form copy lives in the collection story (collection-stories.tsx).
        heading: "Væglamper i glas",
        body: "",
      },
      faqTitle: "Ofte stillede spørgsmål om væglamper i glas",
      faq: [
        {
          q: "Kan jeg sætte en væglampe op uden elektriker?",
          a: "Har væglampen ledning og stik, kan du skrue den fast på væggen og sætte stikket i en almindelig stikkontakt. Det er den nemmeste løsning, hvis du bor til leje. Skal lampen kobles direkte på et udtag i væggen, bør du få en autoriseret elinstallatør til at gøre det.",
        },
        {
          q: "Hvilket glas giver det blødeste lys?",
          a: "Opalglas giver det blødeste lys, fordi det mælkehvide glas spreder lyset jævnt og skjuler pæren. Farvet glas farver lyset og giver en varm stemning, mens klart glas viser pæren og giver mest lys. Vælg en pære med lav lysstyrke og varm farvetemperatur, så glasset kommer til sin ret.",
        },
        {
          q: "Kan en væglampe i glas hænge på badeværelset?",
          a: "Vintage væglamper er sjældent godkendt til de fugtige zoner tæt på bruser og badekar. Ved spejlet uden for de våde zoner kan en væglampe i glas godt fungere, men spørg en elektriker, før du sætter den op. Elektrikeren kan vurdere, om lampen og placeringen passer sammen.",
        },
        {
          q: "Hvor højt skal en væglampe hænge?",
          a: "En god tommelfingerregel er 150 til 170 centimeter over gulvet i entréen og stuen, så lyset falder i øjenhøjde uden at blænde. Ved sengen skal lampen sidde, så lyset rammer bogen, når du sidder op, typisk 30 til 40 centimeter over madrassen.",
        },
      ],
    },
    en: {
      heading: "Glass wall lamps",
      intro:
        "Vintage wall lamps in opal, coloured and hand-blown glass, hand-picked in Copenhagen and shipped worldwide.",
      seo: {
        heading: "Glass wall lamps at Farveladeland",
        body: SHIPPING_EN + "\n\n" + VIEWING_EN,
      },
    },
  },

  "glas-bordlamper": {
    da: {
      metaTitle: "Glas bordlamper | Mundblæst og farvet | Farveladeland",
      metaDescription:
        "Find glas bordlamper hos Farveladeland. Vintage bordlamper i mundblæst italiensk glas, hvor både fod og skærm kan være lavet af glas. Se hele udvalget.",
      heading: "Glas bordlamper",
      intro:
        "Hos Farveladeland finder du vintage bordlamper i glas, alle fundet i Italien. Nogle har en skærm i mælkehvidt glas, andre en fod i farvet glas, og flere er lavet helt i glas fra top til bund. En glas bordlampe giver lys om aftenen og pynter resten af dagen, og den passer både på kommoden, natbordet og skrivebordet.",
      seo: {
        // The long-form copy lives in the collection story (collection-stories.tsx).
        heading: "Glas bordlamper",
        body: "",
      },
      faqTitle: "Ofte stillede spørgsmål om glas bordlamper",
      faq: [
        {
          q: "Er glas eller stof bedst til en bordlampe?",
          a: "Glas giver et klarere og mere levende lys, fordi lyset går gennem materialet og tager farven med sig. En stofskærm dæmper lyset mere og kaster det nedad. Glas holder også til mange års brug uden at falme eller samle støv i fibrene, og det kan vaskes.",
        },
        {
          q: "Hvordan rengør man en glaslampe?",
          a: "Sluk lampen, og lad pæren køle helt af. Tag skærmen af, hvis den kan løsnes, og vask den i lunkent vand med lidt mild opvaskesæbe. Tør den med en blød klud. Glasfoden tørres af med en fugtig klud, så der ikke kommer vand ind til ledningen. Kom aldrig vintage glas i opvaskemaskinen.",
        },
        {
          q: "Hvilken pære passer til en bordlampe i glas?",
          a: "Det afhænger af fatningen, som på de fleste vintage lamper er E14 eller E27. Vælg en LED-pære med varm lysfarve, så bliver glasset ikke så varmt, og farverne står klart. Til klart glas, hvor pæren kan ses, er en dekorativ glødetrådspære et godt valg.",
        },
        {
          q: "Hvor høj skal en bordlampe være?",
          a: "Ved sofaen bør skærmens underkant være i øjenhøjde, når du sidder ned, så lyset ikke blænder. Det svarer typisk til 100 til 110 centimeter over gulvet, når bord og lampe regnes sammen. På natbordet kan lampen være lavere, så længe lyset rammer det, du læser.",
        },
      ],
    },
    en: {
      heading: "Glass table lamps",
      intro:
        "Vintage table lamps in hand-blown glass, hand-picked in Copenhagen and shipped worldwide.",
      seo: {
        heading: "Glass table lamps at Farveladeland",
        body: SHIPPING_EN + "\n\n" + VIEWING_EN,
      },
    },
  },

  "murano-loftlamper": {
    da: {
      intro:
        "Vintage Murano loftlamper i mundblæst glas — fra runde plafonder til skulpturelle pendler. Plads til både stuen, entréen og soveværelset.",
      seo: {
        heading: "Murano loftlamper",
        body:
          "Loftlamperne fra Murano findes i mange former: klassiske runde plafonder tæt på loftet, lange pendler i farvet glas, og mere skulpturelle modeller med flere lyskilder. Fælles for dem alle er det mundblæste glas, der giver et blødt, varmt lys.\n\nVi har både helt små lamper til entréen og store statement-pendler til over spisebordet. Mål altid op først — målene står på den enkelte vare.\n\n" +
          SHIPPING_DA,
        bullets: [
          "E14 eller E27 — står på varen",
          "Original ophængning følger med",
          "Gennemtjekket ledning og fatning",
          "Vi skifter selv den, hvis noget er slidt",
        ],
      },
    },
    en: {
      intro:
        "Vintage Murano ceiling lamps in hand-blown glass — from round flush mounts to sculptural pendants. Right for the living room, hallway and bedroom alike.",
      seo: {
        heading: "Murano ceiling lamps",
        body:
          "Murano ceiling lamps come in many shapes: classic round flush mounts that sit close to the ceiling, long pendants in coloured glass, and more sculptural models with several bulbs. What they share is the hand-blown glass, which gives a soft, warm light.\n\nWe stock everything from very small hallway lamps to big statement pendants for the dining table. Always measure first — dimensions are listed on each product.\n\n" +
          SHIPPING_EN,
        bullets: [
          "E14 or E27 — listed on each piece",
          "Original mounting included",
          "Wiring and socket checked",
          "We replace anything worn before shipping",
        ],
      },
    },
  },

  "murano-lysekrone": {
    da: {
      intro:
        "Vintage Murano lysekroner — fra de elegante Venezia-modeller med blomster og blade til mere moderne 70'er-design.",
      seo: {
        heading: "Murano lysekroner",
        body:
          "En Murano lysekrone er noget af det mest karakteristiske, der er kommet ud af glasværkstederne på øen. De klassiske 'Ca' Rezzonico'-lysekroner med blomster, blade og frugter går helt tilbage til 1700-tallet, men dem vi sælger er typisk fra midten af forrige århundrede.\n\nLysekronerne kommer i mange størrelser. Til en almindelig dansk loftshøjde passer de mindre modeller bedst — de store hører til i opgange, restauranter og høje stuer.\n\n" +
          SHIPPING_DA +
          " Større lysekroner sender vi typisk delvist adskilt for at undgå brud.",
      },
    },
    en: {
      intro:
        "Vintage Murano chandeliers — from the elegant Venetian models with flowers and leaves to more modern 1970s designs.",
      seo: {
        heading: "Murano chandeliers",
        body:
          "A Murano chandelier is one of the most recognisable things to come out of the island's glassworks. The classic 'Ca' Rezzonico' style with flowers, leaves and fruits goes back to the 1700s, though most of what we sell is mid-20th century.\n\nThey come in many sizes. For a standard Danish ceiling height, the smaller models work best — the large ones really belong in stairwells, restaurants and tall living rooms.\n\n" +
          SHIPPING_EN +
          " We usually ship larger chandeliers partly disassembled to avoid breakage.",
      },
    },
  },

  "murano-bordlamper": {
    da: {
      intro:
        "Hos Farveladeland finder du et håndplukket udvalg af vintage Murano bordlamper i mundblæst italiensk glas. Hver lampe er udvalgt én for én, fordi den har noget særligt i farve, form, mønster eller udtryk. En Murano bordlampe er oplagt, hvis du vil tilføre hjemmet personlighed uden at vælge noget masseproduceret. Den kan stå på et natbord, en konsol, et skrivebord, en reol eller som blødt sidelys i stuen.",
      seo: {
        heading: "Murano bordlamper",
        body:
          "Bordlamperne fra Murano dækker alt fra små, runde natbordslamper til store, farverige stuelamper. Mange er signeret af kendte glasmagere som Vistosi, Mazzega eller Seguso, men vi har også mange ikke-signerede stykker af tilsvarende kvalitet.\n\nDe fleste leveres uden skærm, så du selv kan vælge en, der passer til dit hjem. Spørg gerne, hvis du er i tvivl om størrelse.\n\n" +
          SHIPPING_DA,
        bullets: [
          "Mundblæst glas fra Murano, Italien",
          "Vintage — typisk fra 1960–1985",
          "De fleste leveres uden skærm",
          "Tjekket og rengjort før forsendelse",
        ],
      },
    },
    en: {
      intro:
        "Murano table lamps in hand-blown glass — perfect on a console, a bedside table, or as soft side light in the living room.",
      seo: {
        heading: "Murano table lamps",
        body:
          "Our Murano table lamps range from small, round bedside lamps to large, colourful living-room pieces. Many are signed by well-known glassmakers like Vistosi, Mazzega or Seguso, but we also stock plenty of unsigned pieces of the same quality.\n\nMost ship without a shade, so you can pick one that fits your home. Ask us if you are unsure about size.\n\n" +
          SHIPPING_EN,
        bullets: [
          "Hand-blown glass from Murano, Italy",
          "Vintage — typically 1960–1985",
          "Most ship without a shade",
          "Checked and cleaned before shipping",
        ],
      },
    },
  },

  "murano-vaeglamper": {
    da: {
      intro:
        "Murano væglamper og applikker i farvet glas — gode i entré, badeværelse, soveværelse eller parvis langs en gang.",
      seo: {
        heading: "Murano væglamper",
        body:
          "Væglamper fra Murano er især gode steder, hvor en loftlampe ville være for meget: i smalle entréer, på badeværelset eller som læselys ved sengen. Mange af vores væglamper findes i par — så hør os endelig, hvis du leder efter to ens.\n\nVi tjekker fatning og ledning på alle væglamper, før de sendes. Tilslutning skal udføres af en autoriseret elektriker.\n\n" +
          SHIPPING_DA,
        bullets: [
          "Mundblæst glas fra Murano, Italien",
          "Vintage — typisk fra 1960–1985",
          "Mange findes i par",
          "Fatning og ledning tjekket før forsendelse",
        ],
      },
    },
    en: {
      intro:
        "Murano wall lamps and sconces in coloured glass — great in hallways, bathrooms, bedrooms, or in pairs along a corridor.",
      seo: {
        heading: "Murano wall lamps",
        body:
          "Wall lamps from Murano work especially well in places where a ceiling lamp would be too much: narrow hallways, bathrooms, or as reading light next to the bed. Many of ours come in pairs — get in touch if you are looking for two matching pieces.\n\nWe check the socket and wiring on every wall lamp before shipping. Installation must be done by an authorised electrician.\n\n" +
          SHIPPING_EN,
        bullets: [
          "Hand-blown glass from Murano, Italy",
          "Vintage — typically 1960–1985",
          "Many come in pairs",
          "Socket and wiring checked before shipping",
        ],
      },
    },
  },

  "murano-mushroom-lampe": {
    da: {
      intro:
        "Murano mushroom lamper — de runde, svampeformede bordlamper der har defineret 1970'ernes italienske design.",
      seo: {
        heading: "Murano mushroom lamper",
        body:
          "Mushroom lamperne er nok de mest eftertragtede vintage-lamper lige nu. Den runde fod, den buede skærm og det mundblæste glas i lag på lag — det er en form, der både fungerer som lyskilde og som skulptur, når den er slukket.\n\nVi har dem løbende i mange farver og størrelser, fra små natbordsmodeller på 20 cm til store gulvmodeller. De forsvinder hurtigt, så er der en, du har kig på, så slå til.\n\n" +
          SHIPPING_DA,
        bullets: [
          "Italiensk 1970'er-design",
          "Mundblæst glas i lag",
          "Findes i mange farver og størrelser",
          "Sælges typisk inden for få dage",
        ],
      },
    },
    en: {
      intro:
        "Murano mushroom lamps — the round, mushroom-shaped table lamps that defined Italian design in the 1970s.",
      seo: {
        heading: "Murano mushroom lamps",
        body:
          "Mushroom lamps are probably the most sought-after vintage lamps right now. The round base, the domed shade, and the hand-blown glass in layers — it is a shape that works both as a light and as a sculpture when switched off.\n\nWe stock them regularly in many colours and sizes, from small 20 cm bedside models to large floor versions. They sell fast — if you spot one you like, go for it.\n\n" +
          SHIPPING_EN,
        bullets: [
          "Italian 1970s design",
          "Layered hand-blown glass",
          "Many colours and sizes",
          "Usually sold within days",
        ],
      },
    },
  },

  "murano-vaser": {
    da: {
      intro:
        "Vintage Murano vaser i mundblæst glas — fra små sommerfuglevaser til store gulvvaser i farvet og klart glas.",
      seo: {
        heading: "Murano vaser",
        body:
          "Vaserne fra Murano er små stykker glaskunst i sig selv. Vi har klassiske teknikker som sommerso, millefiori og incalmo, men også enkle, ensfarvede vaser, der står flot tomme.\n\nDe fleste er signeret med klistermærke eller graveret bund, men ikke alle — kvaliteten kan dog ses i vægten og glassets klarhed.\n\n" +
          SHIPPING_DA,
        bullets: [
          "Mundblæst glas fra Murano, Italien",
          "Klassiske teknikker: sommerso, millefiori, incalmo",
          "De fleste er signeret på bunden",
          "Ridser og skår står altid i annoncen",
        ],
      },
    },
    en: {
      intro:
        "Vintage Murano vases in hand-blown glass — from small butterfly vases to large floor vases in coloured and clear glass.",
      seo: {
        heading: "Murano vases",
        body:
          "Murano vases are small pieces of glass art in their own right. We carry classic techniques like sommerso, millefiori and incalmo, alongside simple, single-colour vases that look great empty.\n\nMost are signed with a sticker or engraved base, but not all — the quality always shows in the weight and clarity of the glass.\n\n" +
          SHIPPING_EN,
        bullets: [
          "Hand-blown glass from Murano, Italy",
          "Classic techniques: sommerso, millefiori, incalmo",
          "Most are signed on the base",
          "Scratches and chips are always noted",
        ],
      },
    },
  },

  "murano-glaskunst": {
    da: {
      intro:
        "Murano glaskunst — skulpturer, fugle, fisk og dekorative objekter fra øens mest kendte glasmagere.",
      seo: {
        heading: "Murano glaskunst",
        body:
          "Ud over lamper og vaser er Murano kendt for sine små skulpturer i glas: fugle, fisk, frugter, abstrakte former. De er lavet i fri hånd ved ovnen og findes ikke i to ens udgaver.\n\nDe står flot på et bord, en hylde eller en vindueskarm, hvor lyset kan ramme glasset.\n\n" +
          SHIPPING_EN, // (intentionally fall back to EN-Eng line; replaced below)
      },
    },
    en: {
      intro:
        "Murano glass art — sculptures, birds, fish and decorative objects from the island's best-known glassmakers.",
      seo: {
        heading: "Murano glass art",
        body:
          "Beyond lamps and vases, Murano is famous for its small glass sculptures: birds, fish, fruits, abstract forms. They are made freehand at the furnace and no two are alike.\n\nThey look great on a table, a shelf, or a windowsill where the light can hit the glass.\n\n" +
          SHIPPING_EN,
      },
    },
  },

  "murano-pa-udsalg": {
    da: {
      intro:
        "Murano outlet — spar 15–45% på lamper, vaser og glaskunst, der har stået lidt for længe i showroomet.",
      seo: {
        heading: "Murano på udsalg",
        body:
          "Her samler vi de Murano-stykker, der er sat ned. Det kan være lamper med en lille skade i glasset, modeller vi har for mange af, eller bare ting der har stået lidt for længe.\n\nAlle nedsættelser er endelige, og varerne sælges 'som beset'. Eventuelle skader er beskrevet og fotograferet på den enkelte vare.",
        bullets: [
          "Spar 15–45%",
          "Skader er altid beskrevet og vist",
          "Sælges som beset",
          "Returret efter forbrugerkøbeloven gælder stadig",
        ],
      },
    },
    en: {
      intro:
        "Murano outlet — save 15–45% on lamps, vases and glass art that have sat a little too long in the showroom.",
      seo: {
        heading: "Murano on sale",
        body:
          "This is where we gather the Murano pieces we have marked down. It might be lamps with a small flaw in the glass, models we have too many of, or simply things that have sat too long.\n\nAll discounts are final and items are sold 'as seen'. Any damage is described and photographed on each product.",
        bullets: [
          "Save 15–45%",
          "Damage is always described and shown",
          "Sold as seen",
          "Standard 14-day return rights still apply",
        ],
      },
    },
  },

  // Fix for the small bug above — replace the DA glaskunst tail
};

// Patch glaskunst DA copy (use Danish shipping line, not English)
categoryCopy["murano-glaskunst"].da.seo.body =
  "Ud over lamper og vaser er Murano kendt for sine små skulpturer i glas: fugle, fisk, frugter, abstrakte former. De er lavet i fri hånd ved ovnen og findes ikke i to ens udgaver.\n\nDe står flot på et bord, en hylde eller en vindueskarm, hvor lyset kan ramme glasset.\n\n" +
  SHIPPING_DA;

// ──────────────────────────────────────────────────────────
//  VINTAGE LAMPER
// ──────────────────────────────────────────────────────────
Object.assign(categoryCopy, {
  "vintage-lamper": {
    da: {
      intro:
        "Hele vores udvalg af vintage lamper — fra dansk og skandinavisk design til italienske og franske fund. Loft, bord, væg og gulv.",
      seo: {
        heading: "Vintage lamper hos Farveladeland",
        body:
          "Vi har vintage lamper fra hele Europa, men særligt mange fra Danmark, Italien og Frankrig. Det er typisk lamper fra 1950'erne til 1980'erne, og vi udvælger dem en for en — vi har ingen lager fyldt med ens varer.\n\nAlle lamper bliver tjekket og rengjort, før de sendes. Hvis noget er slidt, skifter vi det selv.\n\n" +
          SHIPPING_DA + "\n\n" + VIEWING_DA,
      },
    },
    en: {
      intro:
        "Our full range of vintage lamps — Danish and Scandinavian design alongside Italian and French finds. Ceiling, table, wall and floor.",
      seo: {
        heading: "Vintage lamps at Farveladeland",
        body:
          "We carry vintage lamps from across Europe, with a strong focus on Denmark, Italy and France. Most are from the 1950s through the 1980s, and we pick them one by one — there is no warehouse of identical stock.\n\nEvery lamp is checked and cleaned before shipping. If anything is worn, we replace it ourselves.\n\n" +
          SHIPPING_EN + "\n\n" + VIEWING_EN,
      },
    },
  },
  "vintage-loftlamper": {
    da: {
      intro: "Vintage loftlamper i glas, messing, opal og krom — kuraterede stykker fra 1950'erne og frem.",
      seo: {
        heading: "Vintage loftlamper",
        body:
          "Loftlampen er ofte det første, man lægger mærke til i et rum. Vi har både diskrete plafonder, der sidder tæt på loftet, og længere pendler. Stilarter fra dansk modernisme til italienske 70'er-design.\n\nMål altid op — vi skriver mål, vægt og fatning på hver lampe.\n\n" + SHIPPING_DA,
      },
    },
    en: {
      intro: "Vintage ceiling lamps in glass, brass, opal and chrome — curated pieces from the 1950s onwards.",
      seo: {
        heading: "Vintage ceiling lamps",
        body:
          "The ceiling lamp is often the first thing you notice in a room. We carry both discreet flush mounts that sit close to the ceiling and longer pendants — from Danish modernism to Italian 1970s design.\n\nAlways measure first — we list dimensions, weight and socket on every lamp.\n\n" + SHIPPING_EN,
      },
    },
  },
  "vintage-bordlamper": {
    da: {
      intro: "Vintage bordlamper i alle størrelser — fra små natbordslamper til store statement-stykker.",
      seo: {
        heading: "Vintage bordlamper",
        body:
          "Bordlamperne dækker både små praktiske lyskilder til natbordet og større lamper, der kan stå alene som blikfang i stuen. Vi har dem i glas, keramik, messing og træ.\n\nDe fleste sælges uden skærm, så du selv kan vælge en, der passer til lampen og dit hjem.\n\n" + SHIPPING_DA,
      },
    },
    en: {
      intro: "Vintage table lamps in all sizes — from small bedside lamps to large statement pieces.",
      seo: {
        heading: "Vintage table lamps",
        body:
          "Our table lamps cover both small practical lights for the bedside and larger lamps that stand on their own as a focal point in the living room. We have them in glass, ceramic, brass and wood.\n\nMost ship without a shade, so you can pick one that suits the lamp and your home.\n\n" + SHIPPING_EN,
      },
    },
  },
  "vintage-vaeglamper": {
    da: {
      intro: "Vintage væglamper og applikker — gode i entré, gang og soveværelse, og ofte i par.",
      seo: {
        heading: "Vintage væglamper",
        body:
          "Væglampen frigør plads på borde og natborde og giver et blødere, mere indirekte lys. Vi har både klassiske messinglamper og farverige modeller fra 70'erne.\n\nTilslutning skal udføres af en autoriseret elektriker. Vi tjekker fatning og ledning, før forsendelse.\n\n" + SHIPPING_DA,
      },
    },
    en: {
      intro: "Vintage wall lamps and sconces — great in hallways, corridors and bedrooms, often available as pairs.",
      seo: {
        heading: "Vintage wall lamps",
        body:
          "Wall lamps free up table and bedside space and give a softer, more indirect light. We carry both classic brass sconces and colourful 1970s models.\n\nInstallation must be done by an authorised electrician. We check the socket and wiring before shipping.\n\n" + SHIPPING_EN,
      },
    },
  },
  "stuelamper": {
    da: {
      intro: "Lamper til stuen — pendler, gulv- og bordlamper, der gør hyggen til stuens vigtigste belægning.",
      seo: {
        heading: "Stuelamper",
        body:
          "I stuen handler det om at have flere lyskilder, ikke én stor. Vi anbefaler en blanding: en pendel over sofabordet, en bordlampe eller to på siden, og evt. en gulvlampe i et hjørne.\n\nFind din kombination her — alt er vintage, og vi sender hver mandag.",
      },
    },
    en: {
      intro: "Lamps for the living room — pendants, floor and table lamps that turn cosy lighting into the room's main feature.",
      seo: {
        heading: "Living room lamps",
        body:
          "In the living room, several light sources work better than one big one. We suggest a mix: a pendant over the coffee table, one or two table lamps to the side, and perhaps a floor lamp in a corner.\n\nFind your combination here — all vintage, dispatched every Monday.",
      },
    },
  },
  "spisebordslamper": {
    da: {
      intro: "Spisebordslamper — pendler i passende højde og bredde til at samle bordet om sig.",
      seo: {
        heading: "Spisebordslamper",
        body:
          "En god spisebordslampe hænger 60–70 cm over bordpladen og er ca. en tredjedel så bred som bordet. Lyset skal være varmt og dæmpbart, så maden ser indbydende ud.\n\nVi har både enkle opal-pendler og mere markante Murano-modeller — vælg efter rummets udtryk.\n\n" + SHIPPING_DA,
      },
    },
    en: {
      intro: "Dining table lamps — pendants in the right height and width to gather the table around them.",
      seo: {
        heading: "Dining table lamps",
        body:
          "A good dining lamp hangs 60–70 cm above the tabletop and is about one third as wide as the table. The light should be warm and dimmable so the food looks inviting.\n\nWe carry simple opal pendants and bolder Murano models — pick to match the room.\n\n" + SHIPPING_EN,
      },
    },
  },
  "kokkenlamper": {
    da: {
      intro: "Køkkenlamper — pendler over køkkenø og bord, plus loftlamper med klart, brugbart lys.",
      seo: {
        heading: "Køkkenlamper",
        body:
          "I køkkenet skal lyset både være praktisk og hyggeligt. Til arbejdsbordet skal det være klart og direkte; over spisebordet eller køkkenøen må det gerne være varmere og blødere.\n\nFå tre pendler over en køkkenø — vi har ofte sæt af ens lamper.\n\n" + SHIPPING_DA,
      },
    },
    en: {
      intro: "Kitchen lamps — pendants over the island and table, plus ceiling lamps with clear, useful light.",
      seo: {
        heading: "Kitchen lamps",
        body:
          "In the kitchen, light has to be both practical and cosy. Over the worktop it should be clear and direct; over the dining table or island it can be warmer and softer.\n\nGet three pendants over a kitchen island — we often have sets of matching lamps.\n\n" + SHIPPING_EN,
      },
    },
  },
  "sovevaerelseslamper": {
    da: {
      intro: "Soveværelseslamper — bløde loftlamper, små bordlamper og natbordslamper med varmt lys.",
      seo: {
        heading: "Soveværelseslamper",
        body:
          "Soveværelset skal helst have flere små lyskilder frem for én stor. Vi har bløde opal-loftlamper, små Murano-bordlamper og natbordslamper, der ikke fylder for meget.\n\nVælg pærer med varm farvetemperatur (2700K eller derunder) for et roligt rum at falde i søvn i.",
      },
    },
    en: {
      intro: "Bedroom lamps — soft ceiling lamps, small table lamps and bedside lamps with warm light.",
      seo: {
        heading: "Bedroom lamps",
        body:
          "The bedroom prefers several small light sources over one big one. We have soft opal ceiling lamps, small Murano table lamps and bedside lamps that don't take up too much space.\n\nChoose warm bulbs (2700K or below) for a calm room to fall asleep in.",
      },
    },
  },
  "natbordslamper": {
    da: {
      intro: "Natbordslamper — kompakte vintage-lamper, der passer på selv det mindste natbord.",
      seo: {
        heading: "Natbordslamper",
        body:
          "Natbordslampen skal være lille nok til ikke at vælte vækkeuret, men give lys nok til at læse ved. Vi har både Murano-modeller og enklere skandinaviske lamper i passende størrelse.",
      },
    },
    en: {
      intro: "Bedside lamps — compact vintage lamps that fit even the smallest nightstand.",
      seo: {
        heading: "Bedside lamps",
        body:
          "A bedside lamp needs to be small enough not to knock over the alarm clock, but bright enough to read by. We have both Murano models and simpler Scandinavian lamps in suitable sizes.",
      },
    },
  },
  "skrivebordslamper": {
    da: {
      intro: "Skrivebordslamper — justerbare arkitekt- og kontorlamper med fokuseret arbejdslys.",
      seo: {
        heading: "Skrivebordslamper",
        body:
          "En god skrivebordslampe har en justerbar arm og en skærm, der kan rettes mod arbejdspladsen. Vi har både klassiske arkitektlamper og mere designorienterede vintage-modeller.",
      },
    },
    en: {
      intro: "Desk lamps — adjustable architect and office lamps with focused task light.",
      seo: {
        heading: "Desk lamps",
        body:
          "A good desk lamp has an adjustable arm and a shade that can be aimed at your workspace. We carry both classic architect lamps and more design-led vintage models.",
      },
    },
  },
  "entrelamper": {
    da: {
      intro: "Entrélamper — små loftlamper og væglamper, der byder velkommen uden at fylde hele entréen.",
      seo: {
        heading: "Entrélamper",
        body:
          "I entréen er lampen ofte det første, gæster ser. Vælg en lille loftlampe i opal eller messing, eller et par væglamper, der frigør plads på loftet til at hænge tøj og tasker.",
      },
    },
    en: {
      intro: "Hallway lamps — small ceiling lamps and sconces that welcome you without taking over the hallway.",
      seo: {
        heading: "Hallway lamps",
        body:
          "In the hallway, the lamp is often the first thing guests see. Pick a small opal or brass ceiling lamp, or a pair of sconces to free up the ceiling for hooks and storage.",
      },
    },
  },
  "andre-lamper": {
    da: {
      intro: "Andre vintage lamper — loppefund, sjove enkeltstykker og lamper, der ikke passer i de øvrige kategorier.",
      seo: {
        heading: "Andre lamper",
        body:
          "Her samler vi de lamper, der ikke passer ind under de øvrige kategorier — ofte sjove enkeltstykker fra loppemarkeder og dødsboer. Hvis du leder efter noget anderledes, er det her, du skal kigge.",
      },
    },
    en: {
      intro: "Other vintage lamps — flea market finds, fun one-offs, and lamps that don't fit the other categories.",
      seo: {
        heading: "Other lamps",
        body:
          "This is where we gather lamps that don't fit the other categories — often quirky one-offs from flea markets and estate sales. If you are looking for something different, look here.",
      },
    },
  },

  // ──────────────────────────────────────────────────────────
  //  BORDDÆKNING
  // ──────────────────────────────────────────────────────────
  "borddaekning": {
    da: {
      intro: "Hele vores udvalg af vintage borddækning — tallerkener, fade, skåle, bestik, kander og krus til hverdag og fest.",
      seo: {
        heading: "Vintage borddækning",
        body:
          "Vores borddækning er en blanding af fransk fajance, dansk porcelæn og udvalgte stykker fra resten af Europa. Det meste er fra 1900-tallet, og vi sælger både komplette serier og enkeltdele, så du kan bygge din egen kombination.\n\n" + SHIPPING_DA,
      },
    },
    en: {
      intro: "Our full range of vintage tableware — plates, platters, bowls, cutlery, pitchers and mugs for every day and special occasions.",
      seo: {
        heading: "Vintage tableware",
        body:
          "Our tableware is a mix of French faience, Danish porcelain and selected pieces from the rest of Europe. Most of it is 20th century, and we sell both complete sets and single pieces so you can build your own mix.\n\n" + SHIPPING_EN,
      },
    },
  },
  "bryllupsservice": {
    da: {
      intro: "Bryllupsservice til leje — komplette opsætninger af tallerkener, glas og bestik til jeres bryllup eller fest.",
      seo: {
        heading: "Bryllupsservice",
        body:
          "Vi udlejer vintage bryllupsservice i hele København og resten af Sjælland. I kan vælge en komplet opsætning eller plukke enkelte dele — fade, glas, bestik, lysestager.\n\nSkriv til butik@farveladeland.com med dato og antal gæster, så sender vi et tilbud. Vi henter og bringer, eller I kan afhente selv på Frederiksberg.",
        bullets: [
          "Vintage tallerkener, glas og bestik",
          "Komplette opsætninger eller enkelte dele",
          "Levering på Sjælland",
          "Skriv til butik@farveladeland.com",
        ],
      },
    },
    en: {
      intro: "Wedding rental service — complete settings of plates, glasses and cutlery for your wedding or party.",
      seo: {
        heading: "Wedding service rental",
        body:
          "We rent out vintage wedding tableware across Copenhagen and the rest of Zealand. You can pick a complete setting or just individual pieces — platters, glasses, cutlery, candle holders.\n\nEmail butik@farveladeland.com with date and guest count and we will send you a quote. We deliver and pick up, or you can collect yourself in Frederiksberg.",
        bullets: [
          "Vintage plates, glasses and cutlery",
          "Complete settings or individual pieces",
          "Delivery across Zealand",
          "Email butik@farveladeland.com",
        ],
      },
    },
  },
  "sarreguemines-fajance-porcelaen": {
    da: {
      intro: "Sarreguemines fajance fra Frankrig — tallerkener, fade og skåle med karakteristiske mønstre fra 1800- og 1900-tallet.",
      seo: {
        heading: "Sarreguemines fajance og porcelæn",
        body:
          "Sarreguemines er en fransk fajance-fabrik grundlagt i 1790 i Lorraine. De er især kendt for deres farverige mønstre og motiver — Flore, Favori, Agreste — som passer flot sammen i en mix-and-match opdækning.\n\n" + SHIPPING_DA,
      },
    },
    en: {
      intro: "Sarreguemines faience from France — plates, platters and bowls with characteristic patterns from the 1800s and 1900s.",
      seo: {
        heading: "Sarreguemines faience and porcelain",
        body:
          "Sarreguemines is a French faience manufacturer founded in 1790 in Lorraine. They are best known for their colourful patterns and motifs — Flore, Favori, Agreste — which look beautiful together in a mix-and-match setting.\n\n" + SHIPPING_EN,
      },
    },
  },
  "tallerkener": {
    da: {
      intro: "Vintage tallerkener — middags-, frokost- og dessert-tallerkener i fajance, porcelæn og keramik.",
      seo: {
        heading: "Vintage tallerkener",
        body:
          "Vi har tallerkener i alle størrelser, fra små dessert-tallerkener til store middagstallerkener. Det meste er fransk fajance og dansk porcelæn — sælges enkeltvis, så du kan blande dig frem til din egen opdækning.",
      },
    },
    en: {
      intro: "Vintage plates — dinner, lunch and dessert plates in faience, porcelain and ceramic.",
      seo: {
        heading: "Vintage plates",
        body:
          "We carry plates in every size, from small dessert plates to large dinner plates. Mostly French faience and Danish porcelain — sold individually so you can mix your way to your own setting.",
      },
    },
  },
  "fisketallerkner": {
    da: {
      intro: "Fisketallerkener — dekorative tallerkener med fiskemotiver, perfekt til en sommerfrokost.",
      seo: {
        heading: "Fisketallerkener",
        body:
          "Fisketallerkener er en lille fransk klassiker: smukt malede tallerkener med forskellige fiskemotiver, oprindeligt brugt som menukort til middage med flere fiskeretter. I dag står de flot på væggen eller bruges til en uformel sommerfrokost.",
      },
    },
    en: {
      intro: "Fish plates — decorative plates with fish motifs, perfect for a summer lunch.",
      seo: {
        heading: "Fish plates",
        body:
          "Fish plates are a small French classic: beautifully painted plates with different fish motifs, originally used as menu cards for multi-course fish dinners. Today they look great on the wall or are used for a casual summer lunch.",
      },
    },
  },
  "fade": {
    da: {
      intro: "Vintage fade — serverings- og pyntefade i fajance, porcelæn og glas.",
      seo: {
        heading: "Vintage fade",
        body:
          "Et godt fad er det nemmeste trick til at få en hverdagsret til at se festlig ud. Vi har serveringsfade i mange størrelser, fra små anretningsfade til store ovale platter.",
      },
    },
    en: {
      intro: "Vintage platters — serving and decorative platters in faience, porcelain and glass.",
      seo: {
        heading: "Vintage platters",
        body:
          "A good platter is the easiest way to make an everyday dish look festive. We carry serving platters in many sizes, from small presentation dishes to large oval platters.",
      },
    },
  },
  "skaale": {
    da: {
      intro: "Vintage skåle — fra små frugtskåle til store anretterskåle i fajance og porcelæn.",
      seo: {
        heading: "Vintage skåle",
        body:
          "Skåle er nok det mest brugte i et hjem — frugt, salat, suppe, morgenmad. Vi har dem i mange størrelser og materialer.",
      },
    },
    en: {
      intro: "Vintage bowls — from small fruit bowls to large serving bowls in faience and porcelain.",
      seo: {
        heading: "Vintage bowls",
        body:
          "Bowls are probably the most used pieces in any home — fruit, salad, soup, breakfast. We have them in many sizes and materials.",
      },
    },
  },
  "morgenmadsskale": {
    da: {
      intro: "Morgenmadsskåle — den helt rigtige størrelse til havregrød, müsli og yoghurt.",
      seo: {
        heading: "Morgenmadsskåle",
        body:
          "En god morgenmadsskål skal være rummelig nok til havregrød med toppings, men ikke så stor, at den fylder hele bordet. De fleste af vores er franske eller danske, omkring 14–16 cm i diameter.",
      },
    },
    en: {
      intro: "Breakfast bowls — the right size for porridge, müsli and yoghurt.",
      seo: {
        heading: "Breakfast bowls",
        body:
          "A good breakfast bowl needs to be roomy enough for porridge with toppings, but not so big it takes over the whole table. Most of ours are French or Danish, around 14–16 cm across.",
      },
    },
  },
  "andre-skaale": {
    da: {
      intro:
        "Vintage skåle, fade og opsatser i glas, lertøj, krom og træ — særlige enkeltstykker, der falder uden for de øvrige skål-kategorier. Hånd-plukket på loppemarkeder i Frankrig og Danmark.",
      seo: {
        heading: "Vintage skåle og fade — de skæve enkeltstykker",
        body:
          "Det her er kategorien for de skåle, der ikke kan stå i kø med de andre. Lertøjsskåle med tyk, blå glasur — store nok til både salat og frugt. Lilla glasskåle med tung fod, der står stille på et middagsbord. Fyrretræsskåle med åretegning, vi har slæbt med hjem fra et fransk loppemarked. Krom-frugtskåle fra 70'erne. Italienske fletkurve. Arcoroc-opsatser i klart presset glas — perfekte til kager, frugt eller en stak makroner til kaffen.\n\nDet er sjældent, vi har mere end ét eksemplar af hver. Når en skål er solgt, kommer den typisk ikke igen. Til gengæld er der altid noget nyt på vej — vi henter selv hjem fra Frankrig fire-fem gange om året, og resten dukker op i de danske loppefund vi finder lokalt på Frederiksberg og i resten af København.\n\nSkålene er brugte og bærer præg af et liv før vores. Mindre slidmærker, små afslag eller en patineret kant er en del af charmen — og en del af grunden til prisen. Vi tjekker altid for revner, der gør en skål uegnet til mad eller blomster, og skriver det tydeligt på produktsiden, hvis der er noget at være opmærksom på.",
        bullets: [
          "Lertøjsskåle med blå glasur i flere størrelser — gode som serveringsskåle",
          "Glasopsatser og krom-frugtskåle fra 60'erne og 70'erne",
          "Fyrretræsskåle og fletkurve — varme materialer til en neutral indretning",
          "Sjældne enkeltstykker, der ikke kommer igen, når de først er solgt",
          "Hentes hjem fra Frankrig fire-fem gange om året — resten er danske loppefund",
        ],
      },
    },
    en: {
      intro:
        "Vintage bowls, dishes and footed comports in glass, stoneware, chrome and wood — special one-offs that fall outside the other bowl categories. Hand-picked at flea markets in France and Denmark.",
      seo: {
        heading: "Vintage bowls and dishes — the unusual one-offs",
        body:
          "This is the category for the bowls that don't fit in line with the others. Stoneware bowls with thick blue glaze — big enough for both salad and fruit. Purple glass bowls with a heavy foot that sits still on a dinner table. Pine bowls with visible grain we dragged home from a French flea market. Chrome fruit bowls from the 70s. Italian woven baskets. Arcoroc pressed-glass comports — perfect for cake, fruit or a stack of macarons next to the coffee.\n\nWe rarely have more than one of each. Once a bowl is sold, it usually doesn't come back. On the other hand, there's always something new coming in — we drive down to France four or five times a year, and the rest turn up in the Danish flea-market finds we make locally in Frederiksberg and around Copenhagen.\n\nThe bowls are second-hand and carry traces of a life before ours. Light wear, small chips or a patinated rim is part of the charm — and part of why the price is what it is. We always check for cracks that would make a bowl unfit for food or flowers, and we'll write clearly on the product page if there's anything to be aware of.",
        bullets: [
          "Stoneware bowls with blue glaze in several sizes — good for serving",
          "Glass comports and chrome fruit bowls from the 60s and 70s",
          "Pine bowls and woven baskets — warm materials for a neutral interior",
          "Rare one-offs that won't come back once they're sold",
          "Brought home from France four to five times a year — the rest are local Danish finds",
        ],
      },
    },
  },
  "bestik": {
    da: {
      intro: "Vintage bestik — knive, gafler og skeer i sølv, sølvplet og rustfrit stål.",
      seo: {
        heading: "Vintage bestik",
        body:
          "Vi har bestik fra både Frankrig, Danmark og resten af Europa. Det meste er sølvplet (silver-plated) eller rustfrit stål — sælges enten i sæt eller som enkeltdele.",
      },
    },
    en: {
      intro: "Vintage cutlery — knives, forks and spoons in silver, silver-plate and stainless steel.",
      seo: {
        heading: "Vintage cutlery",
        body:
          "We have cutlery from France, Denmark and the rest of Europe. Mostly silver-plate or stainless steel — sold either in sets or as single pieces.",
      },
    },
  },
  "kander": {
    da: {
      intro: "Vintage kander — vand-, vin- og mælkekander i glas, porcelæn og keramik.",
      seo: {
        heading: "Vintage kander",
        body:
          "En kande på bordet pynter altid — også når den bare står tom. Vi har dem i mange størrelser, fra små mælkekander til store vandkander.",
      },
    },
    en: {
      intro: "Vintage pitchers — water, wine and milk pitchers in glass, porcelain and ceramic.",
      seo: {
        heading: "Vintage pitchers",
        body:
          "A pitcher on the table always looks good — even when it's empty. We have them in many sizes, from small milk pitchers to large water jugs.",
      },
    },
  },
  "keramik-krus-og-kopper": {
    da: {
      intro: "Krus og kopper i keramik og porcelæn — til kaffe, te og kakao.",
      seo: {
        heading: "Krus & kopper",
        body:
          "Et godt krus følger med dig hver morgen. Vi har dem i mange former og glasurer — fra slanke espressokopper til store, rummelige tekrus.",
      },
    },
    en: {
      intro: "Mugs and cups in ceramic and porcelain — for coffee, tea and hot chocolate.",
      seo: {
        heading: "Cups & mugs",
        body:
          "A good mug is with you every morning. We have them in many shapes and glazes — from slim espresso cups to large, roomy tea mugs.",
      },
    },
  },

  // ──────────────────────────────────────────────────────────
  //  GLAS
  // ──────────────────────────────────────────────────────────
  "glas": {
    da: {
      intro: "Hele vores udvalg af vintage glas — vinglas, vandglas, drinksglas, glaskunst og glasskåle.",
      seo: {
        heading: "Vintage glas",
        body:
          "Glas er det nemmeste sted at starte med vintage borddækning. Vi har franske Arcoroc og Luminarc, italiensk glas, og udvalgte stykker fra danske glasværker. Det meste sælges enkeltvis, så du kan bygge dit eget sæt op.\n\n" + SHIPPING_DA,
      },
    },
    en: {
      intro: "Our full range of vintage glass — wine, water and drinks glasses, glass art and glass bowls.",
      seo: {
        heading: "Vintage glass",
        body:
          "Glass is the easiest place to start with vintage tableware. We carry French Arcoroc and Luminarc, Italian glass, and selected pieces from Danish glassworks. Most is sold individually so you can build your own set.\n\n" + SHIPPING_EN,
      },
    },
  },
  "vinglas": {
    da: {
      intro: "Vintage vinglas — rød-, hvid- og rosé-vinglas, fra slanke franske til kraftige danske.",
      seo: {
        heading: "Vintage vinglas",
        body:
          "Et godt vinglas behøver ikke være nyt eller dyrt. Vi har klassiske franske og danske vinglas — sælges enkeltvis, så du kan bygge dit sæt op stille og roligt eller blande forskellige modeller.",
      },
    },
    en: {
      intro: "Vintage wine glasses — red, white and rosé glasses, from slim French to sturdier Danish ones.",
      seo: {
        heading: "Vintage wine glasses",
        body:
          "A good wine glass does not have to be new or expensive. We carry classic French and Danish wine glasses — sold individually so you can build your set slowly or mix different models.",
      },
    },
  },
  "vandglas": {
    da: {
      metaTitle: "Vandglas | vintage vandglas hos Farveladeland",
      metaDescription:
        "Køb håndplukkede vandglas hos Farveladeland. Find vintage glas, farvede vandglas og smukke hverdagsglas til borddækning med personlighed og charme.",
      heading: "Vandglas",
      intro:
        "Hos Farveladeland finder du håndplukkede vandglas med farve, charme og vintagefornemmelse. Vandglas er noget af det, vi bruger mest i hverdagen, men derfor må de gerne være lidt mere særlige end de helt almindelige glas fra skabet.",
      seo: {
        // The long-form copy lives in the collection story (collection-stories.tsx).
        heading: "Vandglas",
        body: "",
      },
      faqTitle: "Ofte stillede spørgsmål om vandglas",
      faq: [
        {
          q: "Hvilke typer vandglas kan jeg finde hos Farveladeland?",
          a: "Udvalget skifter løbende, men du kan blandt andet finde lave vandglas, høje highballglas og glas med massiv eller boblet fod. Nogle er i klart glas, andre i farvet glas som lilla, blå eller gul, og enkelte er i krystalglas. Mange af glassene kommer fra Italien og Frankrig, for eksempel fra franske Arcoroc.",
        },
        {
          q: "Sælges vandglassene enkeltvis eller i sæt?",
          a: "De fleste af vores vandglas sælges stykvis, så du selv kan vælge, hvor mange du har brug for. Enkelte glas sælges i sæt af for eksempel 2 eller 4 styks. Det står altid på produktet, om prisen gælder pr. glas eller for hele sættet.",
        },
        {
          q: "Kan vandglassene tåle opvaskemaskine?",
          a: "De fleste af vores glas kan tåle opvaskemaskine. Farven i vores farvede glas sidder i selve glasset og er ikke malet på, så den ikke vaskes af. Er du i tvivl om et bestemt glas, er du altid velkommen til at kontakte os.",
        },
        {
          q: "Er jeres vandglas vintage?",
          a: "Ja, vores vandglas er vintage eller second-hand fund, som vi selv udvælger. Du skal derfor forvente almindelige brugsspor, og der kan være små forskelle mellem glassene. Vi skriver altid i beskrivelsen, hvis et glas har skår, slag eller andre fejl.",
        },
        {
          q: "Hvordan pakker I vandglas?",
          a: "Vi pakker glassene grundigt i avis, bobleplast og ekstra pap, så de er bedst muligt beskyttet under fragt. Vi sender hver mandag fra showroomet på Frederiksberg og sender til hele verden.",
        },
        {
          q: "Kan jeg se glassene først?",
          a: "Det kan som regel godt lade sig gøre efter aftale. Mange vil gerne se farven og størrelsen i virkeligheden, før de køber. Kontakt os og bestil en fremvisning i vores showroom.",
        },
      ],
    },
    en: {
      intro: "Vintage water glasses — sturdy, attractive everyday glasses for water, juice and iced drinks.",
      seo: {
        heading: "Vintage water glasses",
        body:
          "Water glasses are probably the most used pieces in any home. Ours are typically French or Italian and built for daily use — dishwasher safe too.",
      },
    },
  },
  "drinksglas": {
    da: {
      intro: "Drinksglas — tumblere, highballs og cocktailglas til gin, whisky og spritz.",
      seo: {
        heading: "Drinksglas",
        body:
          "Et godt drinksglas gør hjemmebaren færdig. Vi har både klassiske tumblere til whisky og højere highballs til longdrinks og spritz.",
      },
    },
    en: {
      intro: "Drinks glasses — tumblers, highballs and cocktail glasses for gin, whisky and spritz.",
      seo: {
        heading: "Drinks glasses",
        body:
          "A good drinks glass finishes off the home bar. We have both classic tumblers for whisky and taller highballs for long drinks and spritz.",
      },
    },
  },
  "arcoroc-glas": {
    da: {
      intro: "Arcoroc — fransk glas i tempereret materiale, kendt for sine ikoniske 70'er-modeller.",
      seo: {
        heading: "Arcoroc glas",
        body:
          "Arcoroc er en fransk glas-producent grundlagt i 1958, et søsterbrand til Luminarc. De er kendt for hærdet glas, der tåler både opvaskemaskine og hverdag — og for ikoniske modeller som 'Octime' i sort eller røget glas.",
      },
    },
    en: {
      intro: "Arcoroc — French tempered glass, famous for its iconic 1970s models.",
      seo: {
        heading: "Arcoroc glass",
        body:
          "Arcoroc is a French glass producer founded in 1958, a sister brand to Luminarc. They are known for tempered glass that handles dishwashers and everyday use — and for iconic models like 'Octime' in black or smoked glass.",
      },
    },
  },
  "luminarc-glas": {
    da: {
      intro: "Luminarc — fransk klassiker indenfor klart, robust hverdagsglas.",
      seo: {
        heading: "Luminarc glas",
        body:
          "Luminarc har lavet glas i Arques i Nordfrankrig siden 1948. Det er et af de mest udbredte glasmærker i Europa, og du har sikkert drukket af et Luminarc-glas, uden at vide det.",
      },
    },
    en: {
      intro: "Luminarc — French classic for clear, sturdy everyday glassware.",
      seo: {
        heading: "Luminarc glass",
        body:
          "Luminarc has made glass in Arques in Northern France since 1948. It is one of the most widespread glass brands in Europe — you have probably drunk from a Luminarc glass without realising it.",
      },
    },
  },
  "glaskunst": {
    da: {
      intro: "Glaskunst — vaser, skåle og skulpturer i farvet og klart glas.",
      seo: {
        heading: "Glaskunst",
        body:
          "Vores glaskunst er en blanding af italiensk Murano, skandinavisk og fransk glas. Det er ofte de små stykker, der gør et hjem til ens eget — en farvet skål på vindueskarmen, en signeret vase på reolen.",
      },
    },
    en: {
      intro: "Glass art — vases, bowls and sculptures in coloured and clear glass.",
      seo: {
        heading: "Glass art",
        body:
          "Our glass art is a mix of Italian Murano, Scandinavian and French glass. Often it is the small pieces that make a home feel like your own — a coloured bowl on the windowsill, a signed vase on the shelf.",
      },
    },
  },
  "glasskale": {
    da: {
      intro: "Glasskåle — fra små anretterskåle til store servingsskåle i klart, røget eller farvet glas.",
      seo: {
        heading: "Glasskåle",
        body:
          "En glasskål viser indholdet frem og pynter samtidig. Vi har dem i alle størrelser — fra små skåle til nødder til store skåle til pasta- og frugtsalat.",
      },
    },
    en: {
      intro: "Glass bowls — from small dishes to large serving bowls in clear, smoked or coloured glass.",
      seo: {
        heading: "Glass bowls",
        body:
          "A glass bowl shows off what's inside while looking good itself. We have them in every size — from small dishes for nuts to large bowls for pasta or fruit salad.",
      },
    },
  },

  // ──────────────────────────────────────────────────────────
  //  INTERIØR
  // ──────────────────────────────────────────────────────────
  "interior": {
    da: {
      intro: "Vintage interiør — vaser, krukker, lysestager, spejle, vægpynt og udvalgte møbler.",
      seo: {
        heading: "Vintage interiør",
        body:
          "Det er ofte de små ting, der gør et rum færdigt. Vi har vintage interiør i mange kategorier — vaser og krukker, lysestager, spejle og vægpynt — kurateret en for en.\n\n" + SHIPPING_DA,
      },
    },
    en: {
      intro: "Vintage interior — vases, jars, candle holders, mirrors, wall decor and selected furniture.",
      seo: {
        heading: "Vintage interior",
        body:
          "It is often the small things that finish a room. We carry vintage interior in many categories — vases and jars, candle holders, mirrors and wall decor — picked one by one.\n\n" + SHIPPING_EN,
      },
    },
  },
  "andet-interior": {
    da: {
      intro: "Andet interiør — sjove enkeltstykker, der ikke passer ind i de øvrige interiør-kategorier.",
      seo: {
        heading: "Andet interiør",
        body: "Her finder du de interiørstykker, der har deres egen plads — kuriositeter, dekorative objekter og loppefund.",
      },
    },
    en: {
      intro: "Other interior — quirky one-offs that don't fit the other interior categories.",
      seo: {
        heading: "Other interior",
        body: "This is where you find the interior pieces that stand on their own — curiosities, decorative objects and flea-market finds.",
      },
    },
  },
  "vaser": {
    da: {
      intro: "Vintage vaser — fra små knopvaser til store gulvvaser i glas, keramik og porcelæn.",
      seo: {
        heading: "Vintage vaser",
        body:
          "En vase er smuk i sig selv, også uden blomster. Vi har dem i mange materialer og størrelser, fra små Murano-vaser til store keramik-vaser.",
      },
    },
    en: {
      intro: "Vintage vases — from small bud vases to large floor vases in glass, ceramic and porcelain.",
      seo: {
        heading: "Vintage vases",
        body:
          "A vase is beautiful on its own, even without flowers. We have them in many materials and sizes, from small Murano vases to large ceramic ones.",
      },
    },
  },
  "andre-vaser": {
    da: {
      intro: "Andre vaser — særlige enkeltstykker, der står for sig selv.",
      seo: {
        heading: "Andre vaser",
        body: "Her finder du de vaser, der ikke passer ind under de andre vase-kategorier.",
      },
    },
    en: {
      intro: "Other vases — special one-offs that stand on their own.",
      seo: {
        heading: "Other vases",
        body: "This is where you find vases that don't fit the other vase categories.",
      },
    },
  },
  "krukke": {
    da: {
      intro: "Vintage krukker — opbevarings- og dekorative krukker i keramik, glas og porcelæn.",
      seo: {
        heading: "Vintage krukker",
        body:
          "Krukker bruges både til opbevaring og som rene pyntestykker. Vi har dem i mange størrelser, både franske, italienske og danske.",
      },
    },
    en: {
      intro: "Vintage jars — storage and decorative jars in ceramic, glass and porcelain.",
      seo: {
        heading: "Vintage jars",
        body:
          "Jars are used both for storage and as pure decoration. We have them in many sizes, French, Italian and Danish.",
      },
    },
  },
  "lysestager": {
    da: {
      intro: "Vintage lysestager — fra slanke messing-stager til skulpturelle modeller i keramik og glas.",
      seo: {
        heading: "Vintage lysestager",
        body:
          "Stearinlys er hyggens hurtigste genvej, og en flot lysestage gør det endnu bedre. Vi har dem i mange materialer — messing, sølv, keramik, glas — sælges både enkeltvis og i par.",
      },
    },
    en: {
      intro: "Vintage candle holders — from slim brass holders to sculptural models in ceramic and glass.",
      seo: {
        heading: "Vintage candle holders",
        body:
          "Candles are the fastest shortcut to cosiness, and a beautiful holder makes it even better. We have them in many materials — brass, silver, ceramic, glass — sold both individually and in pairs.",
      },
    },
  },
  "vintage-spejle": {
    da: {
      intro: "Vintage spejle — runde, ovale og firkantede spejle med rammer i træ, messing og glas.",
      seo: {
        heading: "Vintage spejle",
        body:
          "Et spejl gør et rum lysere og større. Vi har spejle i mange former og rammer, fra simple træ-rammer til mere udsmykkede italienske og franske modeller.",
      },
    },
    en: {
      intro: "Vintage mirrors — round, oval and rectangular mirrors with frames in wood, brass and glass.",
      seo: {
        heading: "Vintage mirrors",
        body:
          "A mirror makes a room brighter and bigger. We have mirrors in many shapes and frames, from simple wooden frames to more ornate Italian and French models.",
      },
    },
  },
  "vaegpynt": {
    da: {
      intro: "Vægpynt — billeder, plakater, keramiske relieffer og dekorative tallerkener til væggen.",
      seo: {
        heading: "Vægpynt",
        body:
          "Tomme vægge bliver nemt kedelige. Vi har vægpynt i mange former — gamle plakater, keramiske relieffer, dekorative tallerkener — og enkelte hyggelige loppefund, der bare hører til på en væg.",
      },
    },
    en: {
      intro: "Wall decor — pictures, posters, ceramic reliefs and decorative plates for the wall.",
      seo: {
        heading: "Wall decor",
        body:
          "Empty walls quickly turn boring. We have wall decor in many forms — old posters, ceramic reliefs, decorative plates — and a few cosy flea-market finds that simply belong on a wall.",
      },
    },
  },
  "borde": {
    da: {
      intro: "Vintage borde og udvalgte småmøbler — sofaborde, sidemøbler og enkelte spændende stykker.",
      seo: {
        heading: "Vintage borde og møbler",
        body:
          "Vi har et lille, kurateret udvalg af vintage møbler — primært småmøbler, sofaborde og sideborde. Møbler kan ikke altid sendes som pakkepost; spørg gerne, hvis du er i tvivl om transport.",
      },
    },
    en: {
      intro: "Vintage tables and select small furniture — coffee tables, side pieces and the occasional standout.",
      seo: {
        heading: "Vintage tables and furniture",
        body:
          "We carry a small, curated selection of vintage furniture — mostly small pieces, coffee tables and side tables. Furniture cannot always be shipped as a parcel; please ask if you're unsure about transport.",
      },
    },
  },

  // ──────────────────────────────────────────────────────────
  //  SALE / SPECIAL
  // ──────────────────────────────────────────────────────────
  "abningstilbud": {
    da: {
      intro: "Åbningstilbud — udvalgte varer til særlig pris.",
      seo: {
        heading: "Åbningstilbud",
        body: "Her samler vi løbende udvalgte varer til særlig pris. Tag dem med, mens de er der — vi har dem ikke længe.",
      },
    },
    en: {
      intro: "Special offers — selected pieces at a special price.",
      seo: {
        heading: "Special offers",
        body: "We regularly gather selected pieces here at a special price. Grab them while they last — they don't stay long.",
      },
    },
  },
  "sidste-chance-farvelader": {
    da: {
      intro: "Sidste chance — varer der forsvinder fra sortimentet, sat ekstra ned.",
      seo: {
        heading: "Sidste chance",
        body: "Her er de varer, vi siger farvel til. De er sat ned for at gøre plads til nye fund — når de er væk, er de væk.",
      },
    },
    en: {
      intro: "Last chance — pieces leaving the range, marked down further.",
      seo: {
        heading: "Last chance",
        body: "These are the pieces we are saying goodbye to. They are marked down to make room for new finds — once they're gone, they're gone.",
      },
    },
  },
  "udsalg-under-30-kroner": {
    da: {
      intro: "Under 30 kr. — små vintage-fund til en hyggepris. Perfekt til en lille gave eller et impulskøb.",
      seo: {
        heading: "Vintage under 30 kr.",
        body: "Her samler vi alt, der koster under 30 kroner. Det er typisk små glas, enkelte tallerkener og pyntestykker — gode at smide i kurven sammen med noget større.",
      },
    },
    en: {
      intro: "Under 30 DKK — small vintage finds at a friendly price. Great for a small gift or an impulse buy.",
      seo: {
        heading: "Vintage under 30 DKK",
        body: "We gather everything under 30 kroner here. Typically small glasses, single plates and decorative bits — good to add to your cart alongside something bigger.",
      },
    },
  },
  "nyheder": {
    da: {
      intro: "Netop landet — det nyeste i butikken. Vi får nye fund ind hver uge.",
      seo: {
        heading: "Netop landet",
        body: "Her finder du det allernyeste, vi har sat op i butikken. Vi tilføjer nye varer hver uge, og det er typisk her, de mest populære stykker bliver revet væk hurtigt.",
      },
    },
    en: {
      intro: "Just landed — the latest in store. We add new finds every week.",
      seo: {
        heading: "Just landed",
        body: "Here you find the very latest pieces we have added to the shop. We add new finds every week, and this is typically where the most popular pieces sell out fastest.",
      },
    },
  },
} satisfies CopyMap);

// Restore the original long-form Danish bodies the shopkeeper invested in.
// These were the >100-word descriptions on the old farveladeland.com Shopify
// collections. The English translations stay as the shorter mirrored copy
// (the old site only had Danish long-form text).
import { ORIGINAL_DA_BODIES } from "./category-copy-original";
for (const [handle, body] of Object.entries(ORIGINAL_DA_BODIES)) {
  const entry = categoryCopy[handle];
  if (entry?.da) {
    entry.da.seo.body = body;
  }
}

/**
 * Look up category copy by handle and locale. Returns null if no hand-written
 * copy exists for that handle (the page falls back to a generic intro).
 */
export function getCategoryCopy(handle: string, locale: Locale): CategoryCopy | null {
  const entry = categoryCopy[handle];
  if (!entry) return null;
  return entry[locale];
}
